import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  conectarWhatsApp,
  desconectarWhatsApp,
  enviarWhatsApp,
  getWhatsAppAutomacao,
  getWhatsAppSessao,
  updateWhatsAppAutomacao,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

const rotuloStatus = (status) => {
  switch (status) {
    case 'conectado': return { label: 'Conectado', color: 'success' };
    case 'qr': return { label: 'Aguardando QR', color: 'warning' };
    case 'conectando':
    case 'reconectando': return { label: 'Conectando', color: 'info' };
    case 'indisponivel': return { label: 'Serviço offline', color: 'error' };
    default: return { label: 'Desconectado', color: 'default' };
  }
};

const STATUS_COTA = new Set(['conectado', 'qr', 'conectando', 'reconectando']);

export default function WhatsAppSettingsPage({ estabelecimentoId }) {
  const [sessao, setSessao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState('');
  const [ok, setOk] = useState('');
  const [destino, setDestino] = useState('');
  const [texto, setTexto] = useState('Teste ZenGestão WhatsApp');
  const [resumoDiario, setResumoDiario] = useState(false);
  const [horaEnvio, setHoraEnvio] = useState(8);

  const carregar = useCallback(async () => {
    if (!estabelecimentoId) {
      setLoading(false);
      setErro('Estabelecimento inválido.');
      return;
    }
    try {
      const { data } = await getWhatsAppSessao(estabelecimentoId);
      setSessao(data);
      if (data?.erro) setErro(data.erro);
      else setErro('');
    } catch (e) {
      setErro(getApiErrorMessage(e, 'Não foi possível consultar o WhatsApp.'));
    } finally {
      setLoading(false);
    }
  }, [estabelecimentoId]);

  useEffect(() => {
    setLoading(true);
    carregar();
    (async () => {
      try {
        const { data } = await getWhatsAppAutomacao(estabelecimentoId);
        setResumoDiario(Boolean(data?.resumoVendasDiario));
        setHoraEnvio(Number(data?.horaEnvio ?? 8));
      } catch {
        /* automação opcional */
      }
    })();
  }, [carregar, estabelecimentoId]);

  const status = sessao?.status || 'desconectado';
  const precisaPoll = status === 'qr' || status === 'conectando' || status === 'reconectando';
  const permitidos = Number(sessao?.whatsAppPermitidos ?? 0);
  const emUso = Number(sessao?.whatsAppEmUso ?? 0);
  const jaConta = STATUS_COTA.has(status);
  const cotaEsgotada = permitidos > 0 && !jaConta && emUso >= permitidos;

  useEffect(() => {
    if (!precisaPoll) return undefined;
    const id = setInterval(carregar, 2000);
    return () => clearInterval(id);
  }, [precisaPoll, carregar]);

  const acao = async (fn, sucesso) => {
    setBusy(true);
    setOk('');
    setErro('');
    try {
      const { data } = await fn();
      if (data?.status) setSessao(data);
      if (sucesso) setOk(sucesso);
      await carregar();
    } catch (e) {
      setErro(getApiErrorMessage(e, 'Falha na operação WhatsApp.'));
    } finally {
      setBusy(false);
    }
  };

  const chip = rotuloStatus(status);

  if (!estabelecimentoId) {
    return <Alert severity="info">Salve o estabelecimento antes de conectar o WhatsApp.</Alert>;
  }

  if (loading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (sessao && sessao.configurado === false) {
    return (
      <Alert severity="warning">
        WhatsApp indisponível no momento. Fale com o suporte.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h6">WhatsApp desta filial</Typography>
      <Typography variant="body2" color="text.secondary">
        Cada estabelecimento tem seu próprio número. A quantidade de conexões segue a licença ADD_WHATSAPP
        {permitidos > 0 ? ` (${emUso}/${permitidos} em uso)` : ''}.
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center">
        <Chip size="small" color={chip.color} label={chip.label} />
        {sessao?.numero && (
          <Typography variant="body2">Número: {sessao.numero}</Typography>
        )}
      </Stack>

      {cotaEsgotada && (
        <Alert severity="warning">
          Cota de WhatsApp da licença esgotada ({emUso}/{permitidos}). Desconecte outra filial ou amplie o módulo.
        </Alert>
      )}

      {erro && <Alert severity="error">{erro}</Alert>}
      {ok && <Alert severity="success">{ok}</Alert>}

      {sessao?.qrPng && (
        <Box sx={{ textAlign: 'center' }}>
          <Box
            component="img"
            src={sessao.qrPng}
            alt="QR WhatsApp"
            sx={{ width: 280, height: 280, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
          />
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            WhatsApp no celular → Aparelhos conectados → Conectar um aparelho
          </Typography>
        </Box>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button
          variant="contained"
          disabled={busy || status === 'conectado' || cotaEsgotada}
          onClick={() => acao(() => conectarWhatsApp(estabelecimentoId))}
        >
          Conectar WhatsApp
        </Button>
        <Button
          variant="outlined"
          disabled={busy}
          onClick={() => acao(
            () => desconectarWhatsApp(estabelecimentoId, false),
            'WhatsApp desconectado.'
          )}
        >
          Desconectar
        </Button>
        <Button
          color="error"
          disabled={busy}
          onClick={() => acao(
            () => desconectarWhatsApp(estabelecimentoId, true),
            'Número desconectado. Escaneie o QR para conectar de novo.'
          )}
        >
          Trocar número
        </Button>
      </Stack>

      <Typography variant="subtitle2" sx={{ pt: 1 }}>Enviar teste</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          label="Celular (com DDD)"
          helperText="DDD + celular, ex.: 11999998888"
          value={destino}
          onChange={(e) => setDestino(e.target.value)}
          size="small"
        />
        <TextField
          label="Mensagem"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          size="small"
          fullWidth
        />
        <Button
          variant="contained"
          disabled={busy || status !== 'conectado' || !destino.trim() || !texto.trim()}
          onClick={() => acao(
            () => enviarWhatsApp({
              estabelecimentoId,
              destino,
              texto,
            }),
            'Mensagem enviada.'
          )}
        >
          Enviar
        </Button>
      </Stack>

      <Typography variant="subtitle2" sx={{ pt: 2 }}>Resumo diário de vendas</Typography>
      <Typography variant="body2" color="text.secondary">
        Envia o faturamento do dia anterior desta filial para os usuários com WhatsApp ligado no cadastro.
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={resumoDiario}
            onChange={(e) => setResumoDiario(e.target.checked)}
          />
        }
        label="Enviar resumo todo dia"
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
        <TextField
          select
          label="Horário"
          size="small"
          value={horaEnvio}
          onChange={(e) => setHoraEnvio(Number(e.target.value))}
          sx={{ minWidth: 120 }}
        >
          {Array.from({ length: 24 }, (_, h) => (
            <MenuItem key={h} value={h}>
              {String(h).padStart(2, '0')}:00
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined"
          disabled={busy}
          onClick={() => acao(
            () => updateWhatsAppAutomacao({
              estabelecimentoId,
              resumoVendasDiario: resumoDiario,
              horaEnvio,
            }),
            'Automação salva.'
          )}
        >
          Salvar automação
        </Button>
      </Stack>
    </Stack>
  );
}
